import { css } from '@emotion/react';

const FONT = `'Neue Haas Grotesk Display Pro', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif`;

/* Shared list wrapper: stacks skeleton rows with the same rhythm as real lists. */
export const skeletonListCSS = () => css`
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
    font-family: ${FONT};
`;

/* CotizacionRow skeleton — mirrors the real row layout. */
export const cotizacionRowSkeletonCSS = () => css`
    position: relative;
    background: #ffffff;
    border-radius: 14px;
    border: 1px solid #e2e8f0;
    box-shadow:
        inset 3px 0 0 #cbd5e1,
        0 1px 6px rgba(0, 0, 0, 0.06);
    overflow: hidden;
    margin-bottom: 0.2rem;
    font-family: ${FONT};

    /* Mobile collapsed header — same shape as .cr-header */
    .crs-header {
        display: flex;
        align-items: center;
        padding: 0.75rem 0.9rem;
        gap: 0.8rem;
    }
    .crs-info {
        flex: 1;
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 6px;
    }
    .crs-car-col-mobile {
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        gap: 6px;
        flex-shrink: 0;
    }
    .crs-chevron {
        flex-shrink: 0;
    }
    .crs-mobile-stepper {
        padding: 0.8rem 1rem 1.1rem;
        border-top: 1px solid #f8fafc;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 0.55rem;
    }
    .crs-step {
        flex-shrink: 0;
    }
    .crs-step-seg {
        flex: 0 1 48px;
        height: 4px;
        min-width: 18px;
    }

    /* Desktop grid — matches CotizacionRow.cr-desktop-grid layout */
    .crs-desktop-grid { display: none; }
    @media (min-width: 769px) {
        .crs-header,
        .crs-mobile-stepper { display: none; }

        .crs-desktop-grid {
            display: grid;
            grid-template-columns:
                minmax(180px, 1fr)
                minmax(140px, 0.7fr)
                minmax(140px, 0.6fr)
                auto
                minmax(320px, 1.4fr);
            width: 100%;
            align-items: stretch;
        }
        .crs-col {
            display: flex;
            flex-direction: column;
            gap: 0.5rem;
            min-width: 0;
            padding: 0.7rem 0.85rem;
            justify-content: center;
            border-left: 1px solid #f1f5f9;
        }
        .crs-col-info {
            border-left: none;
            justify-content: space-around;
            min-height: 100px;
            padding: 0.8rem 0.85rem;
            gap: 0.45rem;
        }
        .crs-col-car { gap: 0.45rem; }
        .crs-col-action { gap: 0.35rem; }
        .crs-col-price {
            align-items: flex-end;
            gap: 0.3rem;
            min-width: 110px;
        }
        .crs-stepper-col {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 0.6rem;
            padding: 0.6rem 0.95rem 0.7rem;
            border-left: 1px solid #f1f5f9;
            min-width: 0;
        }
    }
`;

/* KPI Chips skeleton — mirrors the KpiChips bar. */
export const kpiChipsSkeletonCSS = () => css`
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 0.7rem;
    margin: 0 0 1rem;
    font-family: ${FONT};

    .kpis-chip {
        display: flex;
        align-items: center;
        gap: 0.7rem;
        background: #ffffff;
        border: 1px solid #e2e8f0;
        border-radius: 14px;
        padding: 0.85rem 0.9rem;
        box-shadow: 2px 2px 11px rgba(33, 88, 140, 0.04);
        min-width: 0;
    }
    .kpis-chip-body {
        display: flex;
        flex-direction: column;
        gap: 3px;
        flex: 1;
        min-width: 0;
    }
    .kpis-chip-bar {
        margin-top: 3px;
    }

    /* Total: izquierda, span 2 filas, formato grande */
    .kpis-chip.kpis-all {
        grid-row: span 2;
        grid-column: 1;
        flex-direction: column;
        align-items: flex-start;
        justify-content: center;
        gap: 0.5rem;
        padding: 0.85rem 0.9rem;
    }

    @media (max-width: 1050px) {
        grid-template-columns: repeat(3, minmax(0, 1fr));
        .kpis-chip.kpis-all {
            grid-row: span 1;
            grid-column: span 1;
            flex-direction: row;
            align-items: center;
            justify-content: flex-start;
            padding: 0.85rem 0.9rem;
            gap: 0.7rem;
        }
    }

    @media (max-width: 768px) {
        display: flex;
        overflow-x: auto;
        gap: 0.55rem;
        padding: 0.25rem 1rem 0.85rem;
        margin: 0 -1rem 0.6rem;
        scrollbar-width: none;
        &::-webkit-scrollbar { display: none; }
        .kpis-chip { flex: 0 0 160px; }
    }
`;

/* Vendor filter chips skeleton — placeholder mientras carga /vendedores. */
export const vendorFilterSkeletonCSS = () => css`
    display: flex;
    align-items: center;
    gap: 0.45rem;
    flex-wrap: wrap;
    margin: 0 0 0.85rem;
    font-family: ${FONT};

    .vfs-label {
        display: flex;
        align-items: center;
        gap: 6px;
        margin-right: 0.2rem;
        flex-shrink: 0;
    }
    .vfs-chip {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 3px 10px;
        border-radius: 999px;
        border: 1px solid #e2e8f0;
        background: #ffffff;
        min-height: 24px;
    }

    @media (max-width: 768px) {
        flex-wrap: nowrap;
        overflow-x: auto;
        padding: 0.2rem 1rem 0.5rem;
        margin: 0 -1rem 0.6rem;
        scrollbar-width: none;
        &::-webkit-scrollbar { display: none; }
        .vfs-chip { flex: 0 0 auto; }
    }
`;

/* User Dashboard skeleton wrapper. */
export const userDashboardSkeletonCSS = () => css`
    font-family: ${FONT};
    padding: 0.75rem;

    @media (min-width: 768px) {
        padding: 1rem;
    }

    .uds-container {
        max-width: 1500px;
        margin: 0 auto;
    }

    .uds-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 1rem;
        padding: 1rem;
        border-radius: 14px;
        background: #ffffff;
        box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
        gap: 1rem;
    }
    .uds-header-left {
        display: flex;
        flex-direction: column;
        gap: 8px;
        flex: 1;
        min-width: 0;
    }
    .uds-header-right {
        display: none;
        flex-direction: column;
        gap: 8px;
        align-items: flex-end;
    }
    @media (min-width: 768px) {
        .uds-header-right {
            display: flex;
        }
    }

    .uds-body {
        display: flex;
        flex-direction: column;
        gap: 0.75rem;
    }
    @media (min-width: 768px) {
        .uds-body {
            flex-direction: row;
            gap: 1rem;
        }
    }

    .uds-sidebar {
        background: #ffffff;
        border-radius: 14px;
        padding: 1.25rem;
        box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 0.6rem;
        width: 100%;
    }
    @media (min-width: 768px) {
        .uds-sidebar {
            width: 280px;
            flex-shrink: 0;
        }
    }
    .uds-sidebar-stats {
        width: 100%;
        border-top: 1px solid #e5e7eb;
        margin-top: 1rem;
        padding-top: 1rem;
        display: flex;
        flex-direction: column;
        gap: 0.85rem;
    }
    .uds-stat-row {
        display: flex;
        align-items: center;
        gap: 0.75rem;
    }
    .uds-stat-text {
        display: flex;
        flex-direction: column;
        gap: 4px;
        flex: 1;
        min-width: 0;
    }

    .uds-main {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 0.75rem;
    }
    .uds-card {
        background: #ffffff;
        border-radius: 14px;
        padding: 1.25rem;
        box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
    }
    .uds-stepper-row {
        display: flex;
        gap: 0.6rem;
        margin-top: 0.85rem;
        flex-wrap: wrap;
    }
`;
